"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BlackjackGameState = void 0;
exports.calculateStreakMultiplier = calculateStreakMultiplier;
exports.calculateBonusAmount = calculateBonusAmount;
const logic_types_1 = require("./logic-types");
const logic_metrics_1 = require("./logic-metrics");
function calculateStreakMultiplier(streak) {
    var _a;
    const bonusMultipliers = {
        2: 0.2,
        3: 0.3,
        4: 0.5,
        5: 0.8,
        6: 1.3,
    };
    // Return the calculated bonus or use 2.0 for streaks greater than 7
    return streak >= 2 ? ((_a = bonusMultipliers[streak]) !== null && _a !== void 0 ? _a : 2.0) : 0;
}
function calculateBonusAmount(amount, streakMultiplier) {
    return Math.ceil(amount * streakMultiplier);
}
class BlackjackGameState {
    get currentScoreData() {
        return {
            score: this.bankChips,
        };
    }
    get chipsAmount() {
        return this.bankChips;
    }
    get allInAmount() {
        // Find the leftover amount (less than 10 chips)
        const leftover = this.bankChips % logic_metrics_1.LogicMetrics.MinimumChips;
        return this.bankChips - leftover;
    }
    get isOutOfChips() {
        return this.bankChips < logic_metrics_1.LogicMetrics.MinimumChips;
    }
    get isGameCompleted() {
        return this.currentRound === logic_metrics_1.LogicMetrics.TotalRounds;
    }
    makeAnalyticsData() {
        return {
            numberOfRounds: this.currentRound,
            totalScore: this.bankChips,
        };
    }
    constructor(logicEventQueue, analyticsData) {
        this.logicEventQueue = logicEventQueue;
        this.analyticsData = analyticsData;
        this.roundState = logic_types_1.RoundState.Loading;
        this.bankChips = 0;
        this.currentRound = 0;
        this.winStreak = 0;
        this.allowedControls = [];
    }
    initGame(instant) {
        this.depositChips(logic_metrics_1.LogicMetrics.InitialChips, instant);
        this.setWinStreak(0, instant);
        this.increaseCurrentRound(instant);
        this.setRoundState(logic_types_1.RoundState.Deal, instant);
    }
    getRoundState() {
        return this.roundState;
    }
    setRoundState(state, instant) {
        this.roundState = state;
        this.logicEventQueue.enqueue({
            type: "onRoundStateChanged",
            instant,
            payload: {
                state,
                isOutOfChips: this.isOutOfChips,
                isGameCompleted: this.isGameCompleted,
            },
        });
    }
    sendHandResult(player, result, instant) {
        this.setRoundState(logic_types_1.RoundState.Result, instant);
        this.logicEventQueue.enqueue({
            type: "onSetResult",
            instant,
            payload: { player, result },
        });
        this.creditBankForResult(result, player.currentBet, player.hasDoubled, false);
        player.setResult(result);
        this.logicEventQueue.enqueue({
            type: "onCurrentPlayerChanged",
            instant,
            payload: { player: player.clone() },
        });
    }
    moveToNextRound(instant) {
        this.logicEventQueue.enqueue({
            type: "onMoveToNextRound",
            instant,
            payload: undefined,
        });
        this.setRoundState(logic_types_1.RoundState.Continue, instant);
    }
    showGameControls(instant) {
        this.setRoundState(logic_types_1.RoundState.Controls, instant);
    }
    increaseCurrentRound(instant) {
        this.setCurrentRound(++this.currentRound, instant);
        this.analyticsData.numberOfHands++;
    }
    canDoubleBet(currentBet) {
        return currentBet <= this.bankChips;
    }
    canIncreaseBet(value) {
        return this.bankChips >= value;
    }
    canWithdrawChips(value) {
        return this.bankChips >= value;
    }
    creditBankForResult(result, betAmount, hasDoubled, instant) {
        const originalBet = hasDoubled ? betAmount * 2 : betAmount;
        switch (result) {
            case logic_types_1.RoundResult.Push:
                // Note: don't reset win streak
                this.depositChips(originalBet, instant);
                this.analyticsData.numberOfHandsPush++;
                break;
            case logic_types_1.RoundResult.CroupierWin:
                this.setWinStreak(0, instant);
                break;
            case logic_types_1.RoundResult.PlayerWin:
                {
                    const winnings = originalBet;
                    const bonus = calculateBonusAmount(winnings, calculateStreakMultiplier(this.winStreak));
                    this.depositChips(originalBet + winnings + bonus, instant);
                    this.setWinStreak(++this.winStreak, instant);
                    this.analyticsData.totalChipsWon += originalBet;
                    this.analyticsData.totalChipsWinStreakBonus += bonus;
                    this.analyticsData.numberOfHandsWon++;
                }
                break;
            case logic_types_1.RoundResult.PlayerBust:
                this.setWinStreak(0, instant);
                break;
            case logic_types_1.RoundResult.PlayerBlackjack:
                {
                    const winnings = Math.ceil(originalBet * 1.5); // 3:2 payout
                    const bonus = calculateBonusAmount(winnings, calculateStreakMultiplier(this.winStreak));
                    this.depositChips(originalBet + winnings + bonus, instant);
                    this.setWinStreak(++this.winStreak, instant);
                    this.analyticsData.totalChipsWon += originalBet;
                    this.analyticsData.totalChipsBlackjackBonus += winnings - originalBet;
                    this.analyticsData.totalChipsWinStreakBonus += bonus;
                    this.analyticsData.numberOfHandsWon++;
                    this.analyticsData.numberOfNaturalBlackjacks++;
                }
                break;
        }
    }
    withdrawChips(value, instant) {
        this.bankChips -= value;
        this.analyticsData.totalChipsBet += value;
        this.logicEventQueue.enqueue({
            type: "onScoreChanged",
            instant,
            payload: { value: this.bankChips },
        });
        this.logicEventQueue.enqueue({
            type: "onBankChanged",
            instant,
            payload: { value: this.bankChips },
        });
    }
    setAllowedControls(allowedControls) {
        this.allowedControls = allowedControls;
    }
    isAllowed(control) {
        return (this.roundState === logic_types_1.RoundState.Controls &&
            this.allowedControls.includes(control));
    }
    depositChips(value, instant) {
        this.bankChips += value;
        this.analyticsData.maxChipsScoreDuringGame = Math.max(this.analyticsData.maxChipsScoreDuringGame, this.chipsAmount);
        this.logicEventQueue.enqueue({
            type: "onScoreChanged",
            instant,
            payload: { value: this.bankChips },
        });
        this.logicEventQueue.enqueue({
            type: "onBankChanged",
            instant,
            payload: { value: this.bankChips },
        });
    }
    setWinStreak(value, instant) {
        this.winStreak = value;
        this.analyticsData.maxStreak = Math.max(this.analyticsData.maxStreak, this.winStreak);
        this.logicEventQueue.enqueue({
            type: "onWinStreakChanged",
            instant,
            payload: { value: this.winStreak },
        });
    }
    setCurrentRound(value, instant) {
        this.currentRound = value;
        this.logicEventQueue.enqueue({
            type: "onCurrentRoundChanged",
            instant,
            payload: { value: this.currentRound },
        });
    }
}
exports.BlackjackGameState = BlackjackGameState;
